import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { confirmNotice, getNotice, rereadNotice, startReading } from "../api"
import type { ConfirmedNotice, NoticeRead, NoticeState, TaxHeads } from "../contract"
import { Beam } from "../ds/Beam"
import { NiraBar } from "../ds/NiraBar"
import { Rail } from "../answer/Rail"
import { shortDate } from "../answer/format"
import { usePageTitle } from "../answer/hooks"
import { LockIcon } from "../answer/icons"
import { PRIVACY_POINTS, PRIVACY_URL } from "../privacy"
import { ProButton } from "../pro/ProButton"
import { GateLine, gateAction } from "../ds/gate"
import { PAUSED_LINE } from "@/lib/quotaGate"
import { AllowanceHint } from "@/features/billing/components/AllowanceHint"
import { useMeStore } from "@/features/auth/hooks/useCurrentUser"
import { track } from "@/lib/analytics"

const statusOf = (err: unknown) => (err as { status?: number } | null)?.status
import "../answer/answer.css"
import "../answer/answer-v4.css"
import "../answer/pro.css"
import "./case.css"
import "./confirm.css"

const inr = new Intl.NumberFormat("en-IN")
const rupees = (n: number) => `₹${inr.format(Math.round(n))}`
const WORDS = ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"]
const count = (n: number) => WORDS[n] ?? String(n)
const POLL_MS = 2500
const HEADS = ["cgst", "sgst", "igst", "cess"] as const
type Head = (typeof HEADS)[number]

/** The fields that change the outcome: the run waits only for these (handoff 2, flow 1). */
const OUTCOME = new Set(["section_invoked", "periods", "notice_date", "reply_due"])
const blocks = (field: string) => OUTCOME.has(field) || /^A\d+\.(tax|periods)$/.test(field)

interface DraftAllegation {
  id: string
  title: string
  they_say: string
  periods: string
  heads: Record<Head, string>
  interest: number | null
  penalty: number | null
  /** Read as the same money as this earlier allegation; counted once while the amounts still match. */
  paired_with: string | null
}

interface Draft {
  notice_number: string
  notice_date: string
  din: string
  authority: string
  section_invoked: ConfirmedNotice["section_invoked"]
  periods: string
  reply_due: string
  hearing: "yes" | "no" | ""
  allegations: DraftAllegation[]
}

const joinPeriods = (p: string[]) => p.join(" · ")
const splitPeriods = (s: string) => s.split(/[·;\n]/).map((x) => x.trim()).filter(Boolean)
const headText = (v: number | null | undefined) => (v ? String(v) : "")
const amount = (s: string): number | null => {
  const n = Number(s.replace(/[₹,\s]/g, ""))
  return s.trim() && Number.isFinite(n) && n >= 0 ? n : null
}
const taxOf = (a: DraftAllegation): number | null => {
  const values = HEADS.map((h) => amount(a.heads[h])).filter((v): v is number => v !== null && v > 0)
  return values.length ? values.reduce((x, y) => x + y, 0) : null
}

function draftOf(read: NoticeRead | null, confirmed: ConfirmedNotice | null): Draft {
  const readAllegations = read?.allegations ?? []
  const fromRead = (id: string) => readAllegations.find((a) => a.id === id)
  const hearing = confirmed ? confirmed.hearing_offered : read?.hearing.offered ?? null
  const base = confirmed ?? read
  const allegations = confirmed
    ? confirmed.allegations.map((a) => ({
        id: a.id, title: a.title, periods: a.periods, tax: a.tax, interest: a.interest, penalty: a.penalty,
        they_say: fromRead(a.id)?.they_say ?? "", paired_with: fromRead(a.id)?.paired_with ?? null,
      }))
    : readAllegations
  return {
    notice_number: base?.notice_number ?? "",
    notice_date: base?.notice_date ?? "",
    din: base?.din ?? "",
    authority: base?.authority ?? "",
    section_invoked: base?.section_invoked ?? "",
    periods: joinPeriods(base?.periods ?? []),
    reply_due: base?.reply_due ?? "",
    hearing: hearing === true ? "yes" : hearing === false ? "no" : "",
    allegations: allegations.map((a) => ({
      id: a.id,
      title: a.title,
      they_say: a.they_say,
      periods: joinPeriods(a.periods),
      heads: { cgst: headText(a.tax.cgst), sgst: headText(a.tax.sgst), igst: headText(a.tax.igst), cess: headText(a.tax.cess) },
      interest: a.interest,
      penalty: a.penalty,
      paired_with: a.paired_with,
    })),
  }
}

function confirmedOf(d: Draft): ConfirmedNotice {
  return {
    notice_number: d.notice_number.trim(),
    notice_date: d.notice_date,
    din: d.din.trim(),
    authority: d.authority.trim(),
    section_invoked: d.section_invoked,
    periods: splitPeriods(d.periods),
    reply_due: d.reply_due,
    hearing_offered: d.hearing === "yes" ? true : d.hearing === "no" ? false : null,
    allegations: d.allegations.map((a) => ({
      id: a.id,
      title: a.title.trim() || a.id,
      periods: splitPeriods(a.periods),
      tax: Object.fromEntries(HEADS.map((h) => [h, amount(a.heads[h]) || null])) as unknown as TaxHeads,
      interest: a.interest,
      penalty: a.penalty,
    })),
  }
}

/** A pair holds only while both allegations still carry the same tax; an edit that changes one breaks it. */
function livePairs(d: Draft): Record<string, string> {
  const out: Record<string, string> = {}
  for (const a of d.allegations) {
    const other = a.paired_with ? d.allegations.find((b) => b.id === a.paired_with) : undefined
    const tax = taxOf(a)
    if (other && tax !== null && tax === taxOf(other)) out[a.id] = other.id
  }
  return out
}

/** The step before a notice is weighed (NA-Confirm). A dropped notice is not held here: the page
 * says it is being read, and the reading starts on the facts as Nira read them (doubts are flagged
 * on the reading, never asked first). `?check=1` opens the facts to check. */
export default function ConfirmPage() {
  const { caseId = "" } = useParams()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  // Dropped through the Appeal or not door: an order goes straight to its reading.
  const forAppeal = params.get("for") === "appeal"
  const checking = params.get("check") === "1"
  // The reading this check was opened from: the way back to it, without weighing again.
  const from = params.get("from") ?? ""
  const appealStarted = useRef(false)
  const weighStarted = useRef(false)
  const [state, setState] = useState<NoticeState | null>(null)
  const [failed, setFailed] = useState<"" | "gone" | "error">("")
  const [draft, setDraft] = useState<Draft | null>(null)
  const [checked, setChecked] = useState<Set<string>>(new Set())
  const [override, setOverride] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [busy, setBusy] = useState<"" | "reread" | "save" | "appeal" | "weigh">("")
  // The free reading of this kind is used: say so, with the way to Pro, instead of an error.
  const [limit, setLimit] = useState<"" | "notice" | "appeal">("")
  // What the gate said when it stopped a run with nothing to sell (Pro's daily limit), or after "Not now".
  const [limitNote, setLimitNote] = useState("")
  // Two fact re-runs come with the free notice: said beside the button for readers who have a limit on them.
  const factRerunLimited = useMeStore((s) => {
    const m = s.me?.entitlements?.metrics.find((x) => x.metric === "nira.fact_rerun")
    return !!m && m.limit !== null
  })
  const [said, setSaid] = useState("")
  const [histOpen, setHistOpen] = useState(false)
  const seeded = useRef(false)

  const load = useCallback(async () => {
    try {
      const next = await getNotice(caseId)
      setFailed("")
      // The same notice was dropped before: its case file is the one to open, with a re-run.
      if (next.case.merged_into) {
        navigate(`/case/${encodeURIComponent(next.case.merged_into)}`, { replace: true, state: { again: true } })
        return null
      }
      setState(next)
      if (next.status === "ready" && !seeded.current) {
        seeded.current = true
        // The doubts stay marked until the reader says a field is right: the reading went ahead on them.
        setDraft(draftOf(next.read, next.confirmed))
      }
      return next
    } catch (err) {
      setFailed((err as { status?: number })?.status === 404 ? "gone" : "error")
      return null
    }
  }, [caseId, navigate])

  useEffect(() => {
    let alive = true
    let timer: ReturnType<typeof setTimeout> | undefined
    const tick = async () => {
      const next = await load()
      if (alive && next?.status === "reading") timer = setTimeout(() => void tick(), POLL_MS)
    }
    void tick()
    return () => {
      alive = false
      if (timer) clearTimeout(timer)
    }
  }, [load])
  usePageTitle(state ? `Confirm · ${state.case.title}` : "Confirm the facts")

  const read = state?.read ?? null
  // The Appeal or not door, or a document read as an order: the wording is the order's, not the notice's.
  const isOrder = forAppeal || read?.kind === "order" || read?.kind === "order_in_appeal"
  const unsure = useMemo(() => {
    const by: Record<string, string> = {}
    for (const u of read?.unsure ?? []) if (!checked.has(u.field)) by[u.field] = u.why
    return by
  }, [read, checked])
  const waiting = Object.keys(unsure).filter(blocks)
  const pairs = draft ? livePairs(draft) : {}
  const counted = draft ? draft.allegations.filter((a) => !pairs[a.id]) : []
  const tax = counted.reduce((s, a) => s + (taxOf(a) ?? 0), 0)
  const interest = counted.reduce((s, a) => s + (a.interest ?? 0), 0)
  const penalty = counted.reduce((s, a) => s + (a.penalty ?? 0), 0)

  // A fact changed: only then is there anything to weigh again.
  const [edited, setEdited] = useState(false)
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((d) => (d ? { ...d, [key]: value } : d))
    setChecked((c) => new Set(c).add(key))
    setEdited(true)
  }
  const setAllegation = (id: string, patch: Partial<DraftAllegation>, field: "tax" | "periods" | "title") => {
    setDraft((d) => (d ? { ...d, allegations: d.allegations.map((a) => (a.id === id ? { ...a, ...patch } : a)) } : d))
    setChecked((c) => new Set(c).add(`${id}.${field}`))
    setEdited(true)
  }
  const looksRight = (field: string) => setChecked((c) => new Set(c).add(field))

  const reread = async () => {
    setBusy("reread")
    setSaid("")
    try {
      await rereadNotice(caseId)
      seeded.current = false
      setChecked(new Set())
      await load()
    } catch {
      setSaid(`Nira could not read the ${isOrder ? "order" : "notice"}. Try again in a minute.`)
    } finally {
      setBusy("")
    }
  }

  const openAppeal = async () => {
    setBusy("appeal")
    setSaid("")
    track("reading_started", { kind: "appeal", from: forAppeal ? "drop" : "case" })
    // The second appeal check is Pro: the paywall comes before the reading runs, never after.
    const gate = await gateAction("nira.appeal_reading")
    if (!gate.ok) { setBusy(""); setLimitNote(gate.line); setLimit("appeal"); return }
    try {
      const reading = await startReading(caseId, "order_preview")
      navigate(`/case/${encodeURIComponent(caseId)}/reading/${encodeURIComponent(reading.id)}`, { replace: forAppeal })
    } catch (err) {
      setBusy("")
      if (statusOf(err) === 402) setLimit("appeal")
      else setSaid("Couldn't open Appeal or not. Try again.")
    }
  }

  // Facts opened to check, counted once per visit.
  useEffect(() => {
    if (checking) track("facts_checked", { from: from ? "reading" : "case" })
  }, [checking, from])

  useEffect(() => {
    const kind = state?.read?.kind
    if (!forAppeal || appealStarted.current || state?.status !== "ready" || (kind !== "order" && kind !== "order_in_appeal") || !state.read?.order) return
    appealStarted.current = true
    void openAppeal()
    // openAppeal reads only stable values here; run once when the order is ready.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forAppeal, state])

  /** Weigh the notice on the facts as read: the server takes them as the case's notice. */
  const weigh = async () => {
    setBusy("weigh")
    setSaid("")
    track("reading_started", { kind: "notice", from: "drop" })
    // The second notice is Pro: the paywall comes before the reading runs, never after.
    const gate = await gateAction("nira.notice_reading")
    if (!gate.ok) { setBusy(""); setLimitNote(gate.line); setLimit("notice"); return }
    try {
      const reading = await startReading(caseId)
      navigate(`/case/${encodeURIComponent(caseId)}/reading/${encodeURIComponent(reading.id)}`, { replace: true })
    } catch (err) {
      setBusy("")
      if (statusOf(err) === 402) setLimit("notice")
      else setSaid("Couldn't weigh the notice. Try again.")
    }
  }

  useEffect(() => {
    const r = state?.read
    if (checking || forAppeal || weighStarted.current || state?.status !== "ready" || state.confirmed || !r || !(r.is_notice || override)) return
    weighStarted.current = true
    void weigh()
    // weigh reads only stable values here; run once when the notice is read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checking, forAppeal, override, state])

  const save = async () => {
    if (!draft || !state?.document) return
    setBusy("save")
    setSaid("")
    setLimitNote("")
    // The third "Edit facts and re-run" on the free notice is Pro: asked before the facts are saved or anything runs.
    const gate = await gateAction("nira.fact_rerun")
    if (!gate.ok) { setBusy(""); setLimitNote(gate.line); return }
    try {
      await confirmNotice(caseId, state.document.id, confirmedOf(draft))
      const reading = await startReading(caseId)
      navigate(`/case/${encodeURIComponent(caseId)}/reading/${encodeURIComponent(reading.id)}`)
    } catch {
      setBusy("")
      setSaid("Couldn't weigh the notice. Try again.")
    }
  }

  const bar = (
    <>
      <NiraBar
        onMenu={() => setHistOpen((v) => !v)}
        menuOpen={histOpen}
        menuControls="nira-hlist"
        crumbs={[
          { label: "Workspaces", to: "/workspaces" },
          { label: state ? state.case.title : isOrder ? "New order" : "New notice", to: `/workspace/${encodeURIComponent(caseId)}` },
          { label: checking ? "The facts" : isOrder ? "Reading the order" : "Reading the notice" },
        ]}
        beam={state?.status === "reading" || busy === "reread" ? "working" : undefined}
      />
      <Rail threadId="" histOpen={histOpen} onHist={setHistOpen} refreshKey={0} drawerOnly caseId={caseId} scope={forAppeal ? "appeal" : "notice"} />
      <div className={`scrim${histOpen ? " on" : ""}`} aria-hidden="true" onClick={() => setHistOpen(false)} />
    </>
  )
  const caseLink = `/case/${encodeURIComponent(caseId)}`
  const back = from ? `${caseLink}/reading/${encodeURIComponent(from)}` : ""

  if (!state) {
    return (
      <div className="na2 na4 ncf nco">
        {bar}
        <main className="ncf-main">
          {failed === "gone" ? (
            <div className="ncf-msg"><h1>This workspace does not exist, or was deleted.</h1><p><Link to="/ask">Back to Nira</Link></p></div>
          ) : failed ? (
            <div className="ncf-msg"><h1>{isOrder ? "The order could not be opened." : "The notice could not be opened."}</h1><p><button type="button" className="pro-link" onClick={() => void load()}>Try again</button></p></div>
          ) : (
            <p className="ncf-msg" role="status">{isOrder ? "Opening the order…" : "Opening the notice…"}</p>
          )}
        </main>
      </div>
    )
  }

  const doc = state.document
  const wrong = read && !read.is_notice && !override

  return (
    <div className="na2 na4 ncf nco">
      {bar}
      <main className="nco-main">
        <div className="nco-left">
          <p className="ncf-eyebrow">{isOrder ? "The order" : "The notice"}</p>
          {doc ? <DocCard state={state} /> : null}
          <p className="nco-foot">Amounts, sections and dates are read as printed; totals are computed by Nira, never by a model.</p>
        </div>

        <div className="nco-right">
          {limit ? (
            <div className="nco-msg" role="status">
              <h1>{limit === "notice" ? "You have used your free notice analysis." : "You have used your free Appeal or not."}</h1>
              <p>
                {limit === "notice"
                  ? "This notice is saved in its workspace. Pro reads every notice: the time limit, the money and the officer's reading of each allegation."
                  : "This order is saved in its workspace. Pro reads every order for an appeal, with your reply and documents."}
              </p>
              <div className="nco-acts">
                {limitNote && limitNote !== PAUSED_LINE ? <GateLine text={limitNote} /> : <ProButton ranOut source={limit === "notice" ? "notice_limit" : "appeal_limit"} metric={limit === "notice" ? "nira.notice_reading" : "nira.appeal_reading"} />}
                <Link className="ncf-btn" to={caseLink}>Open the workspace</Link>
              </div>
            </div>
          ) : state.status === "none" ? (
            <Message title={isOrder ? "No order in this workspace yet." : "No notice in this workspace yet."}>
              Drop the {isOrder ? "order" : "notice"} in a question or on the workspace, and Nira reads it here. <Link to={caseLink}>Open the workspace</Link>
            </Message>
          ) : state.status === "reading" || busy === "reread" || busy === "weigh" || (!checking && !state.confirmed && state.status === "ready" && read && (read.is_notice || override) && !said) ? (
            <Working weighing={state.status === "ready"} order={isOrder} />
          ) : state.status === "failed" ? (
            <Message title={doc?.status === "failed" ? "The document could not be read." : isOrder ? "Nira could not read the order." : "Nira could not read the notice."}>
              {doc?.status === "failed" ? (
                <>The pages did not come through. Drop the {isOrder ? "order" : "notice"} again on the <Link to={caseLink}>workspace</Link>.</>
              ) : (
                <>Nothing was lost; the document is in the workspace. <button type="button" className="pro-link" onClick={() => void reread()}>Read it again</button></>
              )}
            </Message>
          ) : wrong && read ? (
            <WrongDocument read={read} caseLink={caseLink} onGoOn={() => setOverride(true)} onAppeal={() => void openAppeal()} busy={busy === "appeal"} onReread={() => void reread()} />
          ) : !checking && !state.confirmed && read && (read.is_notice || override) ? (
            <>
              <Working weighing={false} order={isOrder} />
              {said ? <p className="ncf-said" role="status">{said}</p> : null}
              <div className="nco-go">
                <button type="button" className="ncf-btn primary" onClick={() => void weigh()}>Weigh this notice</button>
              </div>
            </>
          ) : draft && read ? (
            <>
              <div className="nco-head">
                {back ? <Link className="nco-back" to={back}>← Back to the reading</Link> : null}
                <p className="ncf-eyebrow">The facts Nira read</p>
                <h1>
                  {waiting.length
                    ? `The facts the reading rests on. Nira read ${count(waiting.length).toLowerCase()} of them with doubt.`
                    : "The facts the reading rests on."}
                </h1>
                <p>Only what changes the outcome: the section, the years, the amounts and the dates. Nothing that identifies you is asked. Change a fact and Nira weighs the notice again; otherwise your reading stays as it is.</p>
              </div>

              <div className="nco-fields">
                <Field label="Notice date" why={unsure.notice_date} onOk={() => looksRight("notice_date")}>
                  <input type="date" value={draft.notice_date} onChange={(e) => set("notice_date", e.target.value)} />
                </Field>
                <Field label="Section invoked" why={unsure.section_invoked} onOk={() => looksRight("section_invoked")}
                  note={read.fraud_pleaded ? "Fraud or suppression pleaded" : undefined}>
                  <select value={draft.section_invoked} onChange={(e) => set("section_invoked", e.target.value as Draft["section_invoked"])}>
                    <option value="">Not stated</option>
                    <option value="73">s.73</option>
                    <option value="74">s.74</option>
                    <option value="74A">s.74A</option>
                  </select>
                </Field>
                <Field label="Reply due" why={unsure.reply_due} onOk={() => looksRight("reply_due")}>
                  <input type="date" value={draft.reply_due} onChange={(e) => set("reply_due", e.target.value)} />
                </Field>
                <Field label="Periods" mono why={unsure.periods} onOk={() => looksRight("periods")}
                  note={read.years.length ? `Financial years: ${read.years.join(", ")}` : undefined}>
                  <input value={draft.periods} onChange={(e) => set("periods", e.target.value)} />
                </Field>
                <Field label="Hearing" why={unsure.hearing ?? unsure["hearing.offered"]} onOk={() => { looksRight("hearing"); looksRight("hearing.offered") }}
                  note={read.hearing.date ? `On ${shortDate(read.hearing.date)}` : undefined}>
                  <select value={draft.hearing} onChange={(e) => set("hearing", e.target.value as Draft["hearing"])}>
                    <option value="yes">Offered in the notice</option>
                    <option value="no">Not offered in the notice</option>
                    <option value="">Not stated</option>
                  </select>
                </Field>
              </div>
              {read.noticees > 1 ? (
                <p className="nco-note">Addressed to {read.noticees} persons. When Nira weighs it, the money is read for each of them, never as one figure.</p>
              ) : null}

              <section className="nco-algs" aria-labelledby="nco-algs-h">
                <p className="ncf-eyebrow" id="nco-algs-h">
                  Allegations · {count(draft.allegations.length).toLowerCase()} read, totals computed
                </p>
                {draft.allegations.length === 0 ? (
                  <p className="ncf-empty">Nira found no demand in this notice. Check the document, or read it again.</p>
                ) : (
                  <div className="nco-table" role="table" aria-label="Allegations">
                    <div className="nco-tr nco-th" role="row">
                      <span role="columnheader">No.</span><span role="columnheader">What they allege</span>
                      <span role="columnheader">Period</span><span role="columnheader" className="r">Tax</span><span role="columnheader" aria-label="Edit" />
                    </div>
                    {draft.allegations.map((a) => {
                      const why = unsure[`${a.id}.tax`] ?? unsure[`${a.id}.periods`]
                      const open = editing === a.id
                      const t = taxOf(a)
                      return (
                        <div key={a.id} className={`nco-row${why ? " unsure" : ""}`} role="rowgroup">
                          <div className="nco-tr" role="row">
                            <span role="cell" className="mono">{a.id}</span>
                            <span role="cell" className="nco-what">
                              {a.title}
                              {pairs[a.id] ? <span className="nco-pair">Same money as {pairs[a.id]}. Nira will count it once.</span> : null}
                              {why ? (
                                <span className="nco-why">
                                  {why.replace(/[.\s]*$/, ".")}{" "}
                                  <button type="button" className="nco-ok" onClick={() => { looksRight(`${a.id}.tax`); looksRight(`${a.id}.periods`) }}>It is right</button>
                                </span>
                              ) : null}
                            </span>
                            <span role="cell" className="mono nco-per">{a.periods || "—"}</span>
                            <span role="cell" className="mono r">{t === null ? "—" : rupees(t)}</span>
                            <span role="cell" className="r">
                              <button type="button" className="nco-edit" aria-expanded={open} onClick={() => setEditing(open ? null : a.id)}>
                                {open ? "Done" : "Edit"}
                              </button>
                            </span>
                          </div>
                          {open ? (
                            <div className="nco-editor">
                              <label className="nco-wide">What they allege
                                <input value={a.title} maxLength={140} onChange={(e) => setAllegation(a.id, { title: e.target.value }, "title")} />
                              </label>
                              <label className="nco-wide">Periods
                                <input className="mono" value={a.periods} onChange={(e) => setAllegation(a.id, { periods: e.target.value }, "periods")} />
                              </label>
                              {HEADS.map((h) => (
                                <label key={h}>{h.toUpperCase()}
                                  <input className="mono" inputMode="numeric" value={a.heads[h]} placeholder="0"
                                    onChange={(e) => setAllegation(a.id, { heads: { ...a.heads, [h]: e.target.value } }, "tax")} />
                                </label>
                              ))}
                              {a.they_say ? <blockquote className="nco-quote">“{a.they_say}”</blockquote> : null}
                            </div>
                          ) : null}
                        </div>
                      )
                    })}
                    <div className="nco-tr nco-total" role="row">
                      <span role="cell" />
                      <span role="cell">
                        Proposed tax{Object.keys(pairs).length ? `, counting ${Object.entries(pairs).map(([b, a]) => `${a} and ${b}`).join(", ")} once` : ""}
                      </span>
                      <span role="cell" />
                      <span role="cell" className="mono r">{tax ? rupees(tax) : "—"}</span>
                      <span role="cell" />
                    </div>
                  </div>
                )}
                <p className="nco-note">
                  {interest || penalty
                    ? `Interest${interest ? ` of ${rupees(interest)}` : ""} and penalty${penalty ? ` of ${rupees(penalty)}` : ""} as proposed in the notice are read separately; Nira recomputes both at the statutory rates.`
                    : "Interest and penalty as proposed in the notice are read separately; Nira recomputes both at the statutory rates."}
                </p>
              </section>

              <section className="ncf-card quiet" aria-labelledby="nco-keep">
                <h2 id="nco-keep">What Nira keeps, and what it does not</h2>
                <p>
                  <b>Kept, under your account:</b> the notice as uploaded, the facts above, every document you add, and each reading.
                  Reusable in research, in the reply and in an appeal without uploading again.
                </p>
                <p>
                  <b>{PRIVACY_POINTS[0].k}.</b> {PRIVACY_POINTS[1].k}, under the DPDP Act, 2023.{" "}
                  <a href={PRIVACY_URL} target="_blank" rel="noreferrer">What Nira does with your documents</a>. Delete the <Link to={caseLink}>workspace</Link> any time: it goes from your account at once, and copies are erased from our systems within 30 days.
                </p>
              </section>

              {said ? <p className="ncf-said" role="status">{said}</p> : null}
              <GateLine text={limitNote} />
              <div className="nco-go">
                {edited || !back ? (
                  <>
                    <button type="button" className="ncf-btn primary" disabled={busy === "save"} onClick={() => void save()}>
                      {busy === "save" ? "Weighing…" : "Weigh it again"}
                    </button>
                    <span>Saves these facts to the workspace, then works out the time limit, the allegations and the money again. Takes a few seconds.{factRerunLimited ? " Two re-runs come with the free notice." : ""}</span>
                    <AllowanceHint metric="nira.fact_rerun" />
                  </>
                ) : (
                  <>
                    <Link className="ncf-btn primary" to={back}>Back to the reading</Link>
                    <span>Nothing changed, so the reading stands as it is.</span>
                  </>
                )}
              </div>
              <p className="nco-again">
                Nira misread the notice itself? <button type="button" className="pro-link" onClick={() => void reread()}>Read it again from the start</button>{" "}
                <span>About a minute. Your reading stays until you weigh it again.</span>
              </p>
            </>
          ) : null}
        </div>
      </main>
    </div>
  )
}

/** While the notice is read and weighed: what Nira needs from it, and what it does with it. */
function Working({ weighing, order = false }: { weighing: boolean; order?: boolean }) {
  const thing = order ? "order" : "notice"
  return (
    <div className="nco-reading" role="status">
      <Beam state="working" size="hero" label={weighing ? `Nira is weighing the ${thing}` : `Nira is reading the ${thing}`} />
      <h1>{weighing ? `Weighing the ${thing}.` : `Reading the ${thing}.`}</h1>
      <p>
        {order
          ? "Nira reads the order: the appeal-by date, the pre-deposit, the forum and the points its own text gives. Nothing about who you are is asked."
          : "Nira copies the section, the years, the amounts and the dates as printed, then works out the time limit and the money. Nothing about who you are is asked."}
      </p>
      <ul className="nco-promise">
        {PRIVACY_POINTS.map((p) => <li key={p.k}><b>{p.k}.</b> {p.v}</li>)}
      </ul>
      <p className="nco-sub">{weighing ? "The reading opens in a few seconds." : "Under a minute. The reading opens on its own when it is done."}</p>
    </div>
  )
}

function Field({ label, mono, why, note, onOk, children }: {
  label: string; mono?: boolean; why?: string; note?: string; onOk: () => void; children: React.ReactNode
}) {
  return (
    <label className={`nco-field${why ? " unsure" : ""}${mono ? " mono" : ""}`}>
      <span className="nco-l">{label}</span>
      {children}
      {why ? (
        <span className="nco-why">
          {why.replace(/[.\s]*$/, ".")}{" "}
          <button type="button" className="nco-ok" onClick={(e) => { e.preventDefault(); onOk() }}>It is right</button>
        </span>
      ) : note ? <span className="nco-sub">{note}</span> : null}
    </label>
  )
}

function Message({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="nco-msg">
      <h1>{title}</h1>
      <p>{children}</p>
    </div>
  )
}

/** The document this page is about, by name: its first page's text is not shown, since nothing
 * on it (the addressee, the office) bears on the reading, and the facts that do are on the right. */
function DocCard({ state }: { state: NoticeState }) {
  const doc = state.document!
  return (
    <div className="nco-doc">
      <span className="mono" title={doc.filename}>{doc.filename}</span>
      <span>{doc.pages ? `${doc.pages} ${doc.pages === 1 ? "page" : "pages"} · ` : ""}<LockIcon />kept under your account</span>
    </div>
  )
}

const WRONG: Record<NoticeRead["kind"], { title: string; body: (r: NoticeRead) => string }> = {
  order: {
    title: "This looks like an Order-in-Original, not a notice.",
    body: (r) =>
      `It has findings and a demand confirmed${r.notice_date ? `, dated ${shortDate(r.notice_date)}` : ""}${r.notice_number ? `, order no. ${r.notice_number}` : ""}. A notice is analysed for what the officer will decide; an order is read for whether to appeal.`,
  },
  reply: {
    title: "This looks like a reply to a notice, not the notice.",
    body: () => "Nira never analyses a reply as if it were the notice. The reply stays in the workspace and is read with the notice; drop the notice itself to weigh it.",
  },
  drc01a: {
    title: "This looks like an intimation in DRC-01A, not a notice.",
    body: () => "A DRC-01A comes before the show cause notice: it says what the officer intends to demand, and paying or replying now can end it there. Nira weighs the show cause notice; when it comes, drop it in this workspace.",
  },
  asmt10: {
    title: "This looks like a scrutiny notice in ASMT-10, not a show cause notice.",
    body: () => "An ASMT-10 asks you to explain discrepancies in your returns; there is no demand to weigh yet. Ask research how to answer it, and drop the show cause notice here if one follows.",
  },
  order_in_appeal: {
    title: "This looks like an Order-in-Appeal, not a notice.",
    body: (r) =>
      `It decides an appeal${r.notice_date ? `, dated ${shortDate(r.notice_date)}` : ""}${r.notice_number ? `, order no. ${r.notice_number}` : ""}. The next appeal from it lies to the GST Appellate Tribunal; Nira reads it for whether to go there.`,
  },
  show_cause_notice: { title: "", body: () => "" },
  other: {
    title: "This does not look like a notice.",
    body: () => "Nira weighs show cause notices. The document stays in the workspace; drop the notice itself to weigh it.",
  },
}

function WrongDocument({ read, caseLink, onGoOn, onAppeal, onReread, busy }: {
  read: NoticeRead; caseLink: string; onGoOn: () => void; onAppeal: () => void; onReread: () => void; busy: boolean
}) {
  const copy = WRONG[read.kind]
  const isOrder = read.kind === "order" || read.kind === "order_in_appeal"
  const orderRead = isOrder && !!read.order
  return (
    <div className="nco-wrong" role="group" aria-labelledby="nco-wrong-h">
      <p className="ncf-eyebrow">Not a notice</p>
      <h1 id="nco-wrong-h">{copy.title}</h1>
      <p>{copy.body(read)}</p>
      <div className="nco-acts">
        {orderRead ? (
          <button type="button" className="ncf-btn primary" disabled={busy} onClick={onAppeal}>{busy ? "Opening…" : "Open Appeal or not"}</button>
        ) : isOrder ? (
          <button type="button" className="ncf-btn primary" onClick={onReread}>Read the order again</button>
        ) : (
          <Link className="ncf-btn primary" to={caseLink}>Open the workspace</Link>
        )}
        <button type="button" className="ncf-btn" onClick={onGoOn}>It is a notice, go on</button>
      </div>
    </div>
  )
}
